/*
 * Influencer affiliate attribution (see site-dashboard-2026
 * docs/superpowers/specs/2026-09-22-influencer-affiliate-pages-design.md).
 *
 * On an influencer page view (sections/pj_influencer-page.liquid sets
 * document.documentElement.dataset.pjInfluencer) write the first-party cookie
 *   pj_inf=<handle>|<unix-seconds>   (30 days, last touch — always overwrite)
 * On EVERY page load, when the cookie is present, make sure the cart carries
 *   _influencer     = <handle>
 *   _influencer_at  = <ISO timestamp of the view>
 * as hidden cart attributes (the leading underscore hides them from cart and
 * checkout; they reach the order as custom attributes, which is what the
 * mission-control ingest reads). Runs once per page plus after Dawn's
 * cartUpdate event, so a cart created by a quick-add on this very page is
 * stamped too. Dependency-free; also loadable in Node for tests.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  root.PJInfluencer = api;
  if (typeof document !== "undefined" && typeof window !== "undefined") api.boot(window, document);
})(typeof window !== "undefined" ? window : globalThis, function () {
  var COOKIE_NAME = "pj_inf";
  var WINDOW_SECONDS = 30 * 24 * 60 * 60; // 2592000
  var ATTR_HANDLE = "_influencer";
  var ATTR_AT = "_influencer_at";
  var HANDLE_RE = /^[a-z0-9._-]{1,64}$/;
  // Shopify serves metaobject web pages at /pages/<urlHandle>/<entry-handle> (urlHandle = creator)
  // (verified live 2026-09-25); the bare /creator/ form is kept for a
  // vanity redirect target or a future route change.
  var PATH_RE = /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?(?:\/pages)?\/creator\/([^\/?#]+)/i;

  function normalizeHandle(raw) {
    if (typeof raw !== "string") return null;
    var h = raw.trim().toLowerCase();
    return HANDLE_RE.test(h) ? h : null;
  }

  function handleFromPath(pathname) {
    if (typeof pathname !== "string") return null;
    var m = PATH_RE.exec(pathname);
    return m ? normalizeHandle(decodeURIComponent(m[1])) : null;
  }

  function buildCookie(handle, nowMs) {
    var sec = Math.floor(nowMs / 1000);
    return (
      COOKIE_NAME + "=" + handle + "|" + sec +
      "; Max-Age=" + WINDOW_SECONDS + "; Path=/; SameSite=Lax; Secure"
    );
  }

  function parseCookie(cookieHeader, nowMs) {
    if (typeof cookieHeader !== "string" || !cookieHeader) return null;
    var parts = cookieHeader.split(";");
    for (var i = 0; i < parts.length; i++) {
      var kv = parts[i].trim();
      if (kv.indexOf(COOKIE_NAME + "=") !== 0) continue;
      var value = kv.slice(COOKIE_NAME.length + 1);
      var bar = value.indexOf("|");
      if (bar < 0) return null;
      var handle = normalizeHandle(value.slice(0, bar));
      var atSec = parseInt(value.slice(bar + 1), 10);
      if (!handle || !isFinite(atSec)) return null;
      var nowSec = Math.floor(nowMs / 1000);
      if (nowSec - atSec > WINDOW_SECONDS) return null;
      return { handle: handle, atSec: atSec };
    }
    return null;
  }

  function desiredAttributes(parsed) {
    var out = {};
    out[ATTR_HANDLE] = parsed.handle;
    out[ATTR_AT] = new Date(parsed.atSec * 1000).toISOString();
    return out;
  }

  function needsStamp(cartAttributes, desired) {
    if (!cartAttributes || typeof cartAttributes !== "object") return true;
    return (
      cartAttributes[ATTR_HANDLE] !== desired[ATTR_HANDLE] ||
      cartAttributes[ATTR_AT] !== desired[ATTR_AT]
    );
  }

  function cartUrls(routes) {
    var base = (routes && routes.cart_url) || "/cart";
    var update = (routes && routes.cart_update_url) || "/cart/update";
    return { get: base + ".js", update: update + ".js" };
  }

  function stampCart(fetchFn, routes, parsed) {
    var urls = cartUrls(routes);
    var desired = desiredAttributes(parsed);
    return fetchFn(urls.get, { credentials: "same-origin" })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (!needsStamp(cart && cart.attributes, desired)) return "skipped";
        return fetchFn(urls.update, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ attributes: desired }),
        }).then(function () { return "stamped"; });
      });
  }

  function boot(win, doc) {
    function run() {
      var now = Date.now();
      var pageHandle =
        normalizeHandle(doc.documentElement && doc.documentElement.dataset && doc.documentElement.dataset.pjInfluencer) ||
        handleFromPath(win.location && win.location.pathname);
      if (pageHandle) {
        try {
          doc.cookie = buildCookie(pageHandle, now);
        } catch (e) { /* a browser that throws on cookie access must not break the page */ }
      }
      var parsed = parseCookie(doc.cookie, now);
      if (!parsed || typeof win.fetch !== "function") return;
      var stamp = function () {
        stampCart(win.fetch.bind(win), win.routes, parsed).catch(function () { /* never break the page */ });
      };
      stamp();
      // PUB_SUB_EVENTS/subscribe are declared with top-level const/function in
      // classic deferred scripts (constants.js / pubsub.js). `function` becomes
      // a window property; a top-level `const` does not — so win.PUB_SUB_EVENTS
      // is always undefined. Resolve both as bare globals (falling back to the
      // win properties so a test can still inject them) rather than reading
      // them off win. If this module runs before those scripts, sub/events are
      // unavailable here — the page-load stamp above already ran, so that's fine.
      var events = typeof PUB_SUB_EVENTS !== "undefined" ? PUB_SUB_EVENTS : win.PUB_SUB_EVENTS;
      var sub = typeof subscribe === "function" ? subscribe : win.subscribe;
      if (typeof sub === "function" && events && events.cartUpdate) {
        sub(events.cartUpdate, stamp);
      }
    }
    if (doc.readyState === "loading") doc.addEventListener("DOMContentLoaded", run);
    else run();
  }

  return {
    COOKIE_NAME: COOKIE_NAME,
    WINDOW_SECONDS: WINDOW_SECONDS,
    ATTR_HANDLE: ATTR_HANDLE,
    ATTR_AT: ATTR_AT,
    normalizeHandle: normalizeHandle,
    handleFromPath: handleFromPath,
    buildCookie: buildCookie,
    parseCookie: parseCookie,
    desiredAttributes: desiredAttributes,
    needsStamp: needsStamp,
    stampCart: stampCart,
    boot: boot,
  };
});
